import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from "@material-ui/core";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const getStatusFromNivel = nivel => {
  const map = {
    baixo: "cold",
    medio: "warm",
    alto: "hot",
    muito_alto: "urgent"
  };

  return map[nivel] || "unknown";
};

const getStatusLabel = status => {
  const labels = {
    cold: "Frio",
    warm: "Morno",
    hot: "Quente",
    urgent: "Urgente",
    risk: "Risco",
    support: "Suporte",
    no_opportunity: "Sem oportunidade",
    unknown: "Indefinido"
  };

  return labels[status] || "Indefinido";
};

const getIntentLabel = intent => {
  const labels = {
    purchase_intent: "Intenção de compra",
    price_request: "Pedido de preço",
    objection: "Objeção",
    complaint: "Reclamação",
    support: "Suporte",
    scheduling: "Agendamento",
    payment: "Pagamento",
    cancellation: "Cancelamento",
    follow_up: "Follow-up",
    no_opportunity: "Sem oportunidade",
    unknown: "Indefinida"
  };

  return labels[intent] || "Indefinida";
};

const getStatusColor = status => {
  const colors = {
    cold: "#64748b",
    warm: "#f59e0b",
    hot: "#ef4444",
    urgent: "#dc2626",
    risk: "#7c2d12",
    support: "#2563eb",
    no_opportunity: "#6b7280",
    unknown: "#7c3aed"
  };

  return colors[status] || "#7c3aed";
};

const detectIntent = data => {
  const text = `${data?.oportunidade || ""} ${data?.resumo || ""}`.toLowerCase();

  if (text.includes("preço") || text.includes("valor") || text.includes("orçamento")) {
    return "price_request";
  }

  if (text.includes("compr") || text.includes("venda") || text.includes("contratar") || text.includes("renovação")) {
    return "purchase_intent";
  }

  if (text.includes("cancel")) {
    return "cancellation";
  }

  if (text.includes("boleto") || text.includes("pagamento")) {
    return "payment";
  }

  if (text.includes("suporte") || text.includes("erro") || text.includes("problema")) {
    return "support";
  }

  return "unknown";
};

const normalizeAnalysis = data => {
  const status = getStatusFromNivel(data?.nivel);
  const score = Number(data?.score || 0);

  return {
    score,
    status,
    intent: detectIntent(data),
    summary: data?.resumo || "Nenhum resumo retornado.",
    nextAction: data?.proximaAcao || data?.sugestaoAbordagem || "Nenhuma ação recomendada.",
    suggestedReply: data?.mensagemSugerida || "",
    risk:
      score < 40
        ? "Oportunidade baixa ou pouco clara. Recomenda-se revisar a conversa antes de abordar."
        : "Nenhum risco crítico identificado.",
    tags: Array.isArray(data?.sinaisDetectados) ? data.sinaisDetectados : [],
    raw: data
  };
};

const OpportunityRadarInputButton = ({ ticketId, disabled, onUseReply }) => {
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const handleAnalyze = async () => {
    if (!ticketId) {
      toast.warning("Ticket não encontrado para análise.");
      return;
    }

    try {
      setLoading(true);

      const { data } = await api.post(`/tickets/${ticketId}/opportunity-radar`);

      setAnalysis(normalizeAnalysis(data));
      setModalOpen(true);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUseReply = () => {
    if (!analysis?.suggestedReply) {
      toast.warning("Nenhuma resposta sugerida disponível.");
      return;
    }

    if (typeof onUseReply === "function") {
      onUseReply(analysis.suggestedReply);
    }

    setModalOpen(false);
  };

  const statusColor = getStatusColor(analysis?.status);

  return (
    <>
      <Tooltip title="Radar de Oportunidades IA">
        <IconButton
          onClick={handleAnalyze}
          disabled={disabled || loading}
          size="small"
        >
          {loading ? (
            <CircularProgress size={18} />
          ) : (
            <span
              style={{
                fontSize: 18,
                fontWeight: "bold",
                color: "#0f766e"
              }}
            >
              🎯
            </span>
          )}
        </IconButton>
      </Tooltip>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Radar de Oportunidades com IA</DialogTitle>

        <DialogContent dividers>
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8, marginBottom: 12 }}>
            <Chip
              label={`Score: ${analysis?.score ?? 0}%`}
              style={{ backgroundColor: statusColor, color: "#fff", fontWeight: 700 }}
            />

            <Chip
              label={`Status: ${getStatusLabel(analysis?.status)}`}
              variant="outlined"
              style={{ borderColor: statusColor, color: statusColor, fontWeight: 700 }}
            />

            <Chip
              label={`Intenção: ${getIntentLabel(analysis?.intent)}`}
              variant="outlined"
            />
          </Box>

          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
            Resumo
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 12, whiteSpace: "pre-wrap" }}>
            {analysis?.summary || "Nenhum resumo retornado."}
          </Typography>

          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
            Próxima melhor ação
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 12, whiteSpace: "pre-wrap" }}>
            {analysis?.nextAction || "Nenhuma ação recomendada."}
          </Typography>

          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
            Resposta sugerida
          </Typography>
          <Paper
            variant="outlined"
            style={{
              padding: 12,
              marginBottom: 12,
              background: "#f8fafc",
              whiteSpace: "pre-wrap"
            }}
          >
            <Typography variant="body2">
              {analysis?.suggestedReply || "Nenhuma resposta sugerida."}
            </Typography>
          </Paper>

          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
            Risco / atenção
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 12, whiteSpace: "pre-wrap" }}>
            {analysis?.risk || "Nenhum risco identificado."}
          </Typography>

          {Array.isArray(analysis?.tags) && analysis.tags.length > 0 && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 6 }}>
                {analysis.tags.map((tag, index) => (
                  <Chip
                    key={`${tag}-${index}`}
                    size="small"
                    label={tag}
                    variant="outlined"
                  />
                ))}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>
            Fechar
          </Button>

          <Button
            color="primary"
            variant="contained"
            onClick={handleUseReply}
            disabled={!analysis?.suggestedReply}
          >
            Usar resposta sugerida
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OpportunityRadarInputButton;
